<script setup lang="ts">
  import { ref, onMounted, onUnmounted, computed } from 'vue'
  import MessageServiceCn from '@/components/AppMessageCn/messageCn'
  import _fetch from '@/utils/fetch'
  import { eventBus } from '@/utils/eventBus'
  import { useUserStore } from '@zaohaowu/website-common/stores/userStore'
  import { useTicketStore } from '@zaohaowu/website-common/stores/userTicket'
  import trackUtil from '@/track/edulogin'
  import trackEvent from '@/track/trackEvent'
  defineOptions({ name: 'LoginEdu' })
  const props = defineProps({
    canClose: {
      type: Boolean,
      default: true
    }
  })

  const emit = defineEmits(['close', 'ok'])
  const publicKey = '***_shda***_shda'
  import Utf8 from 'crypto-js/enc-utf8'
  import AES from 'crypto-js/aes'
  import ECB from 'crypto-js/mode-ecb'
  import Pkcs7 from 'crypto-js/pad-pkcs7'

  const schoolLoginVisible = ref(true) // 默认不显示
  const schoolAccount = ref('')
  const schoolPassword = ref('')
  const loading = ref(false)
  let signing = ref(false)
  const userStore = useUserStore()
  const ticketStore = useTicketStore()

  // 处理关闭
  const handleClose = () => {
    if (loading.value) return
    schoolLoginVisible.value = false
  }

  const handleKeyup = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      schoolLogin()
    }
  }

  const handlePassword = (event: Event) => {
    event.stopPropagation()
    const target = event.target as HTMLInputElement
    const pre = target.previousSibling as HTMLInputElement
    if (pre) {
      const type = pre.type
      if (type === 'password') {
        pre.type = 'text'
        target.className = 'password-icon close'
      } else {
        pre.type = 'password'
        target.className = 'password-icon'
      }
    }
  }

  // 登录成功回调
  const loginCallback = (res: any) => {
    // 更新登录状态
    window.isLoggedIn = true
    userStore.setUser(res.data)
    ticketStore.setTicket(res.data.balance || '0')
    // 触发成功回调
    emit('ok', res.data)
    // 触发通知挂载
    eventBus.trigger('mountNotice')
  }

  const schoolLogin = async () => {
    // 埋点
    trackUtil.clickLogin()
    if (signing.value) return
    if (!schoolAccount.value || !schoolPassword.value) {
      MessageServiceCn({
        message: '请输入正确的账号和密码',
        type: 'error'
      })
      return
    }
    signing.value = true
    try {
      const response = await _fetch('/aigc/api/auth/edu/login', {
        method: 'POST',
        body: JSON.stringify({
          username: schoolAccount.value,
          password: encryptAes(schoolPassword.value)
        })
      })
      const res = await response.json()
      if (res.code === 200) {
        // 埋点-登录成功
        trackUtil.loginSuccess(res.data.userId)
        trackEvent.track('config', {
          user_unique_id: res.data.userId,
          user_name: res.data.nickName
        })
        loginCallback(res)
      } else {
        MessageServiceCn({
          message: res.msg || '登录失败',
          type: 'error'
        })
        // 埋点-登录失败
        trackUtil.loginFail(res.code)
      }
    } catch (error) {
      MessageServiceCn({
        message: (error as string) || '登录失败，请重试',
        type: 'error'
      })
      // 埋点
      trackUtil.loginFail(error as string)
    } finally {
      signing.value = false
    }
  }
  // 加密
  function encryptAes(data: string): string {
    const aesKey = publicKey
    if (aesKey && data) {
      const key = Utf8.parse(aesKey)
      const result = AES.encrypt(data, key, {
        mode: ECB,
        padding: Pkcs7
      }).toString()
      return result.replace(/\+/g, '{cand}')
    } else {
      return data
    }
  }

  // 计算按钮状态
  const isAllSchoolInputsFilled = computed(() => {
    return schoolAccount.value.trim() && schoolPassword.value.trim()
  })

  onMounted(() => {})

  onUnmounted(() => {})
</script>

<template>
  <main id="login_edu_container">
    <div v-if="schoolLoginVisible" class="login-mask-container">
      <div class="login-container">
        <a v-if="props.canClose" class="detail-close" @click.stop="handleClose">
          <span class="hitems-close"></span>
        </a>
        <div class="school-login-header">
          <img class="login-logo" src="@/assets/images/logo-zhw.webp" alt="logo" />
          <span class="school-login-title">教育公益版</span>
        </div>
        <div class="login-types">
          <div class="login-btn">
            <input
              v-model="schoolAccount"
              @keyup="handleKeyup"
              class="login-input"
              type="text"
              placeholder="账号"
            />
          </div>
          <div class="login-btn">
            <input
              v-model="schoolPassword"
              @keyup="handleKeyup"
              name="schoolPassword"
              class="login-input"
              type="password"
              placeholder="密码"
            />
            <a
              class="password-icon icon-with-text"
              style="margin-right: 11px"
              @click="handlePassword"
            ></a>
          </div>
          <div
            @click="schoolLogin"
            class="login-btn sign-btn"
            :class="{
              'btn-active': isAllSchoolInputsFilled,
              'btn-disabled': !isAllSchoolInputsFilled,
              'signing': signing
            }"
          >
            登录
            <!-- {{ signing ? '登录中...' : '登录' }} -->
          </div>
        </div>
        <div class="school-tips-container">
          <div class="school-tips-item">
            嘿！小朋友，来百度搜索“
            <a class="link" href="https://zaohaowu.com" target="_blank">造好物</a>
            ”，
          </div>
          <div class="school-tips-item">用同一个账号登录，解锁超多好玩的活动！</div>
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped lang="stylus">
  @import './index.styl'
</style>
